import { useState } from "react"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { Tag as TagIcon, Upload, X } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import { PERMISSION_LABELS, PermissionState, type Tag } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Empty, Header, Person, SearchBox, SelectField, Stats, Status } from "@/components/audience/primitives"
import { formatCount } from "@/lib/download"
import { api, ApiError } from "@/lib/api"
import { relativeTime } from "@/lib/format"
import { contactsQuery, contactsStatsQuery, segmentQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/contacts/")({
  validateSearch: z.object({ segment: z.string().optional(), tag: z.string().optional() }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(contactsStatsQuery(params.workspace)),
  component: ContactDirectory,
})

const PERMISSION_FILTERS = [{ value: "any", label: "Any permission" }, ...PermissionState.options.map((value) => ({ value, label: PERMISSION_LABELS[value] }))]

/** Screen 30. */
function ContactDirectory() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const canTag = can("reply") || can("edit_automations")
  const { segment: segmentId, tag: tagFromSearch } = Route.useSearch()
  const queryClient = useQueryClient()
  const stats = useSuspenseQuery(contactsStatsQuery(workspace.slug)).data
  const segment = useQuery({ ...segmentQuery(workspace.slug, segmentId ?? ""), enabled: Boolean(segmentId) })
  const [search, setSearch] = useState("")
  const [tagId, setTagId] = useState<string>(tagFromSearch ?? "all")
  const [permission, setPermission] = useState("any")
  const [cursor, setCursor] = useState<string | undefined>()
  const [selected, setSelected] = useState<string[]>([])
  const [tagDialog, setTagDialog] = useState(false)
  const [tagChoice, setTagChoice] = useState<string>("new")
  const [tagName, setTagName] = useState("Follow up")
  const filters = { q: search || undefined, tagId: tagId === "all" ? undefined : tagId, permission: permission === "any" ? undefined : permission, segmentId, cursor }
  const page = useQuery({ ...contactsQuery(workspace.slug, filters), placeholderData: (previous) => previous })
  const rows = page.data?.contacts ?? []
  const params = { workspace: workspace.slug }
  const tabTags = stats.tags.slice(0, 4)

  const tagMutation = useMutation({
    mutationFn: (input: { contactIds: string[]; tagId?: string; tagName?: string }) => api<Tag>(`/w/${workspace.slug}/contacts/tag`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: async (tag, input) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "contacts"] })
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "tags"] })
      setSelected([])
      setTagDialog(false)
      toast.success(`${input.contactIds.length} contact${input.contactIds.length === 1 ? "" : "s"} tagged with ${tag.name}`)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not add the tag"),
  })

  return (
    <div className="page-wrap au-root">
      <Header title="Contacts" description={segment.data ? `Segment: ${segment.data.name} · ${segment.data.summary}` : "The people behind every conversation."}>
        {segmentId && (
          <Button variant="ghost" asChild>
            <Link to="/w/$workspace/contacts" params={params} search={{}}>
              <X /> Clear segment
            </Link>
          </Button>
        )}
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/contacts/segments" params={params}>
            Segments
          </Link>
        </Button>
        {can("manage_customer_data") && (
          <Button asChild>
            <Link to="/w/$workspace/contacts/import" params={params}>
              <Upload /> Import contacts
            </Link>
          </Button>
        )}
      </Header>
      <Stats
        items={[
          { label: "People", value: formatCount(stats.people), detail: "One profile per person" },
          { label: "Channel identities", value: formatCount(stats.identities), detail: "Instagram and Messenger" },
          { label: "Saved segments", value: formatCount(stats.segments), detail: "Membership updates automatically" },
        ]}
      />
      <Tabs
        value={tagId}
        onValueChange={(value) => {
          setTagId(value)
          setCursor(undefined)
        }}
        className="mt-7"
      >
        <TabsList variant="line" className="au-tab-line">
          <TabsTrigger value="all">All contacts</TabsTrigger>
          {tabTags.map((tag) => (
            <TabsTrigger value={tag.id} key={tag.id}>
              {tag.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="au-toolbar">
        <SearchBox
          value={search}
          onChange={(value) => {
            setSearch(value)
            setCursor(undefined)
          }}
          placeholder="Search name, email or handle…"
        />
        <div className="ml-auto w-44">
          <SelectField
            label="Marketing permission filter"
            value={permission}
            onChange={(value) => {
              setPermission(value)
              setCursor(undefined)
            }}
            options={PERMISSION_FILTERS}
          />
        </div>
      </div>
      {selected.length > 0 && canTag && (
        <div className="mb-3 flex items-center gap-3 rounded-lg bg-muted p-2 text-sm">
          <span>{selected.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setTagDialog(true)}>
            <TagIcon /> Add tag
          </Button>
          <Button size="icon-sm" variant="ghost" aria-label="Clear selection" className="ml-auto" onClick={() => setSelected([])}>
            <X />
          </Button>
        </div>
      )}
      <div className="au-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox aria-label="Select visible contacts" checked={rows.length > 0 && rows.every((row) => selected.includes(row.id))} onCheckedChange={(value) => setSelected(value === true ? rows.map((row) => row.id) : [])} />
              </TableHead>
              <TableHead>Person</TableHead>
              <TableHead>Channel identities</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>IG marketing permission</TableHead>
              <TableHead>Last active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Checkbox aria-label={`Select ${row.displayName}`} checked={selected.includes(row.id)} onCheckedChange={(value) => setSelected(value === true ? [...selected, row.id] : selected.filter((id) => id !== row.id))} />
                </TableCell>
                <TableCell>
                  <Person name={row.displayName} detail={row.detail} workspace={workspace.slug} contactId={row.id} />
                </TableCell>
                <TableCell className="text-muted-foreground">{row.identityLabel}</TableCell>
                <TableCell>
                  <span className="flex flex-wrap gap-1">
                    {row.tags.map((tag) => (
                      <Badge variant="secondary" className="font-normal" key={tag.id}>
                        {tag.name}
                      </Badge>
                    ))}
                  </span>
                </TableCell>
                <TableCell>
                  <Status>{PERMISSION_LABELS[row.marketing.instagram ?? "unknown"]}</Status>
                </TableCell>
                <TableCell className="text-muted-foreground">{row.lastActiveAt ? relativeTime(row.lastActiveAt) : "No activity"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {page.isSuccess && !rows.length && <Empty noun="contacts" hint={stats.people === 0 ? "Contacts appear when customers message you, or import a CSV." : "Try another search or reset your filters."} />}
      </div>
      <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          Showing {rows.length} of {formatCount(page.data?.total ?? 0)} contacts. Imported handles are reference data until verified.
        </span>
        {cursor && (
          <Button variant="ghost" size="sm" onClick={() => setCursor(undefined)}>
            First page
          </Button>
        )}
        {page.data?.nextCursor && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => setCursor(page.data?.nextCursor ?? undefined)}>
            Next page
          </Button>
        )}
      </div>
      <Dialog open={tagDialog} onOpenChange={setTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a tag</DialogTitle>
            <DialogDescription>Tags describe people. They never change marketing permission.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag-choice">Tag</Label>
              <SelectField label="Tag" value={tagChoice} onChange={setTagChoice} options={[{ value: "new", label: "New tag…" }, ...stats.tags.map((t) => ({ value: t.id, label: t.name }))]} />
            </div>
            {tagChoice === "new" && (
              <div className="space-y-2">
                <Label htmlFor="tag-name">Tag name</Label>
                <Input id="tag-name" value={tagName} onChange={(e) => setTagName(e.target.value)} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialog(false)}>
              Cancel
            </Button>
            <Button disabled={tagMutation.isPending || (tagChoice === "new" && !tagName.trim())} onClick={() => tagMutation.mutate(tagChoice === "new" ? { contactIds: selected, tagName: tagName.trim() } : { contactIds: selected, tagId: tagChoice })}>
              Add tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
